import { useState, useEffect, useCallback } from 'react';

import { CartButton } from '@zooplus/ui-components';

import usePageTracking from '@hooks/use-adobe-tracking/use-page-tracking';
import { useBrowserInfo } from '@hooks/use-browser-info';
import commonStyles from '@styles/common.module.scss';
import { TRACKING_DATA_EVENT, TARGET_ACTION_ID, TARGET_COMPONENT_NAME } from '@utilities/constants';

const PLACE_ORDER_URL = '/checkout/place-order';

interface CheckoutPlaceOrderButtonProps {
  customerHash: string;
  orderPreviewGrandTotal: string;
  csrfToken: string;
  isDisabled: boolean;
  isLoading: boolean;
  buttonText: string;
}

const CheckoutPlaceOrderButton: React.FC<CheckoutPlaceOrderButtonProps> = (props) => {
  const { isDisabled, orderPreviewGrandTotal, isLoading, buttonText, csrfToken, customerHash } =
    props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trackEventUsingTrackerListener } = usePageTracking();
  const browserInfo = useBrowserInfo();

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      if (isLoading || isSubmitting) {
        e.preventDefault();
        return;
      }
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.CLICK,
        TARGET_ACTION_ID.PLACE_ORDER_BUTTON,
        TARGET_COMPONENT_NAME.PLACE_ORDER,
      );
      setIsSubmitting(true);
    },
    [isLoading, isSubmitting, trackEventUsingTrackerListener],
  );

  useEffect(() => {
    isDisabled &&
      trackEventUsingTrackerListener(
        TRACKING_DATA_EVENT.IMPRESSION,
        TARGET_ACTION_ID.PLACE_ORDER_BUTTON_DISABLED,
        TARGET_COMPONENT_NAME.PLACE_ORDER,
      );
  }, [trackEventUsingTrackerListener, isDisabled]);

  return (
    <form
      action={PLACE_ORDER_URL}
      method="POST"
      onSubmit={handleFormSubmit}
      noValidate
      data-testid="prepayment-form">
      <input type="hidden" name="customerHash" value={customerHash} />
      <input type="hidden" name="orderPreviewGrandTotal" value={orderPreviewGrandTotal} />
      <input type="hidden" name="X-CSRF-Token" value={csrfToken} />
      <input type="hidden" name="browserInfo" value={browserInfo} />
      <CartButton
        type="submit"
        className={commonStyles.cartButton}
        disabled={isDisabled}
        isLoading={isSubmitting}
        aria-label={buttonText}
        data-testid="place-order-button">
        {buttonText}
      </CartButton>
    </form>
  );
};

export default CheckoutPlaceOrderButton;
